import { ARTICLES, articlesInSection, searchArticles, type Article } from "@/data/articles";
import { sectionById } from "@/data/sections";
import { Link, useDocumentTitle, useLocation } from "@/lib/router";
import { CardStory, ListStory } from "@/components/Story";
import { EmiCalculator, Newsletter, Trending } from "@/components/Sidebar";

function ListingLayout({ title, blurb, items }: { title: string; blurb?: string; items: Article[] }) {
  const [first, second, third, ...rest] = items;
  return (
    <main className="mx-auto max-w-[1240px] px-4 pt-6">
      <div className="border-b-[3px] border-ink pb-3">
        <h1 className="splash text-[3rem] sm:text-[4.2rem]">{title}</h1>
        {blurb && <p className="mt-1 font-[family-name:var(--font-body)] text-lg text-ink-soft">{blurb}</p>}
      </div>

      {items.length === 0 ? (
        <p className="py-16 text-center text-lg text-ink-soft">
          No stories found. <Link href="/" className="font-bold text-brand underline">Back to the front page</Link>
        </p>
      ) : (
        <div className="mt-6 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-9">
            {first && (
              <div className="grid gap-6 md:grid-cols-3">
                <div className="md:col-span-2">
                  <CardStory a={first} size="lg" />
                </div>
                <div className="space-y-6">
                  {second && <CardStory a={second} size="sm" />}
                  {third && <CardStory a={third} size="sm" />}
                </div>
              </div>
            )}
            {rest.length > 0 && (
              <div className="mt-8 border-t border-rule">
                {rest.map(a => (
                  <ListStory key={a.slug} a={a} />
                ))}
              </div>
            )}
          </div>
          <aside className="space-y-8 lg:col-span-3">
            <Trending />
            <EmiCalculator />
            <Newsletter />
          </aside>
        </div>
      )}
    </main>
  );
}

export function SectionPage({ id }: { id: string }) {
  const section = sectionById(id);
  useDocumentTitle(`${section?.label ?? "Section"} | BrickBharat`);
  if (!section) return <NotFound />;
  return <ListingLayout title={section.label} blurb={section.blurb} items={articlesInSection(id)} />;
}

export function VideoPage() {
  useDocumentTitle("Video | BrickBharat");
  return <ListingLayout title="Video" blurb="Home tours, explainers and the view from above." items={ARTICLES.filter(a => a.video)} />;
}

export function SearchPage() {
  const { search } = useLocation();
  const q = search.get("q") ?? "";
  useDocumentTitle(`Search: ${q} | BrickBharat`);
  const results = searchArticles(q);
  return <ListingLayout title={`"${q}"`} blurb={`${results.length} ${results.length === 1 ? "story" : "stories"} found`} items={results} />;
}

export function NotFound() {
  useDocumentTitle("Page not found | BrickBharat");
  return (
    <main className="mx-auto max-w-[1240px] px-4 py-20 text-center">
      <p className="splash text-[6rem] text-brand">404</p>
      <h1 className="headline text-3xl">This page has been demolished.</h1>
      <p className="mt-3 text-ink-soft">
        <Link href="/" className="font-bold text-brand underline">Head back to the front page</Link>
      </p>
    </main>
  );
}
